import { Controller } from "@hotwired/stimulus"

// ATLAS map controller -- plots RT places with clustering, filtering, and click popups.
// Relies on Mapkick/Mapbox GL being loaded (window.mapboxgl available).
export default class extends Controller {
  static targets = ["count", "nullCount", "boundsCount", "autoSubmit", "searchButton", "actionRow", "filterActiveDot", "groupByToggle", "clustering", "legend", "legendTitle", "legendBody", "drawer", "selectionCount", "actionActiveDot", "zoomInput", "zoomSlider"]
  static values = {
    placesUrl: String,
    mapId: String,
    mapStyle: String,
    groupBy: String,
    groupLabels: { type: Object, default: {} },
    clustering: { type: Boolean, default: true },
    labelMinZoom: { type: Number, default: 12 }
  }

  static PALETTE = [
    "#2563eb", "#dc2626", "#16a34a", "#d97706",
    "#7c3aed", "#0891b2", "#db2777", "#65a30d",
    "#ea580c", "#4f46e5", "#0d9488", "#be185d"
  ]
  static UNASSIGNED_COLOR = "#94a3b8"

  connect() {
    this.popup = null
    this.hoverPopup = null
    this._selectedIds = new Set()
    this._draw = null
    this._escHandler = (e) => {
      if (e.key !== "Escape") return
      if (this._selectedIds?.size > 0) {
        this.clearSelection()
        return
      }
      this.closeFilters()
    }
    document.addEventListener("keydown", this._escHandler)
    this._savedStyleKey = this.readSavedStyleKey()
    this.syncActionRow()
    this.syncFilterActiveDot()
    this._savedGroupBy = this.readSavedGroupBy()
    if (this._savedGroupBy && this.hasGroupByToggleTarget) {
      this.groupByToggleTargets.forEach(el => {
        el.checked = el.value === this._savedGroupBy
      })
      this.groupByValue = this._savedGroupBy
    }
    const savedClustering = this.readSavedClustering()
    if (savedClustering !== null) {
      this.clusteringValue = savedClustering
      if (this.hasClusteringTarget) this.clusteringTarget.checked = savedClustering
    }
    const savedLabelZoom = this.readSavedLabelZoom()
    if (savedLabelZoom !== null) {
      this.labelMinZoomValue = savedLabelZoom
    }
    this.markActiveLabelZoom(this.labelMinZoomValue)
    setTimeout(() => this.initLayer(), 400)
  }

  disconnect() {
    this.popup?.remove()
    this.hoverPopup?.remove()
    document.removeEventListener("keydown", this._escHandler)
    if (this._draw) {
      try { this.getMap()?.removeControl(this._draw) } catch (_) {}
      this._draw = null
    }
  }

  closeFilters() {
    const menu = this.element.querySelector("[data-dropdown-target='menu']")
    menu?.classList.add("hidden")
  }

  filterChanged() {
    this.syncFilterActiveDot()
    if (!this.hasAutoSubmitTarget || this.autoSubmitTarget.checked) {
      clearTimeout(this._filterDebounce)
      this._filterDebounce = setTimeout(() => this.reloadPlaces(), 250)
    }
  }

  syncFilterActiveDot() {
    if (!this.hasFilterActiveDotTarget) return
    const anyActive = Array.from(this.element.querySelectorAll("[data-atlas-filter]")).some(el => {
      if (el.tagName === "INPUT") return el.value.trim() !== ""
      if (el.tagName === "SELECT") return Array.from(el.selectedOptions).some(o => o.value !== "")
      return false
    })
    this.filterActiveDotTarget.classList.toggle("hidden", !anyActive)
  }

  search() {
    this.reloadPlaces()
  }

  autoSubmitChanged() {
    this.syncActionRow()
    // If auto-update was just turned on, reload immediately so the map reflects current filters.
    if (this.hasAutoSubmitTarget && this.autoSubmitTarget.checked) this.reloadPlaces()
  }

  syncActionRow() {
    if (!this.hasActionRowTarget || !this.hasAutoSubmitTarget) return
    const auto = this.autoSubmitTarget.checked
    if (this.hasSearchButtonTarget) this.searchButtonTarget.classList.toggle("hidden", auto)
    this.actionRowTarget.classList.toggle("grid-cols-1", auto)
    this.actionRowTarget.classList.toggle("grid-cols-3", !auto)
  }

  clear() {
    this.element.querySelectorAll("[data-atlas-filter]").forEach(el => {
      if (el.tagName === "INPUT") {
        el.value = ""
      } else if (el.tagName === "SELECT") {
        const slimCtrl = window.Stimulus?.getControllerForElementAndIdentifier?.(el, "slim")
        if (slimCtrl?.select) {
          slimCtrl.select.setSelected([])
        } else {
          Array.from(el.options).forEach(o => o.selected = false)
        }
      }
    })
    this.syncFilterActiveDot()
    this.reloadPlaces()
  }

  groupByToggleChanged(event) {
    const selected = event.target.checked ? event.target.value : ""
    // Mutual exclusion: clicking one unchecks the others.
    if (this.hasGroupByToggleTarget) {
      this.groupByToggleTargets.forEach(el => {
        if (el !== event.target) el.checked = false
      })
    }
    this.groupByValue = selected
    this.saveGroupBy(selected)
    this.applyGroupBy()
  }

  clusteringChanged(event) {
    const on = event.target.checked
    this.clusteringValue = on
    this.saveClustering(on)
    this.resetMapLayers()
    this.reloadPlaces()
  }

  resetMapLayers() {
    const map = this.getMap()
    if (!map) return
    ;["atlas-labels", "atlas-points", "atlas-cluster-count", "atlas-clusters"].forEach(id => {
      if (map.getLayer(id)) map.removeLayer(id)
    })
    if (map.getSource("atlas-places")) map.removeSource("atlas-places")
  }

  reloadPlaces() {
    const map = this.getMap()
    if (!map) return

    const url = new URL(this.placesUrlValue, window.location.origin)
    this.applyFilters(url)

    fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(r => r.json())
      .then(geojson => {
        this.updateCounts(geojson.meta)
        const withCoords = {
          ...geojson,
          features: geojson.features.filter(f => f.geometry)
        }
        this._lastGeojson = withCoords
        const enriched = this.assignGroupColors(withCoords, this.groupByValue)
        const source = map.getSource("atlas-places")
        if (source) {
          source.setData(enriched)
        } else {
          this.addPlacesLayer(map, enriched)
        }
        this.updateLegend(enriched.features)
        if (this._skipNextFit) {
          this._skipNextFit = false
        } else {
          this.fitToData(map, withCoords.features)
        }
        // Re-assert selection filter + drawer visibility after data reloads, so the
        // highlighted overlay and drawer count don't drift when filters change.
        this.applySelectionLayerFilter()
        this.syncSelectionUI()
      })
      .catch(e => console.warn("[atlas] load failed", e))
  }

  // --- zoom widget ---

  zoomStep(event) {
    event?.preventDefault?.()
    const map = this.getMap()
    if (!map) return
    const step = parseFloat(event?.params?.step ?? "0") || 0
    if (!step) return
    const current = map.getZoom()
    const next = Math.max(0, Math.min(22, current + step))
    map.setZoom(next)
    this.syncZoomControls()
  }

  zoomToSlider(event) {
    const value = parseFloat(event?.target?.value)
    if (!Number.isFinite(value)) return
    this.getMap()?.setZoom(value)
    this.syncZoomControls()
  }

  selectZoomInput(event) {
    event?.target?.select?.()
  }

  zoomToInput(event) {
    if (event?.type === "keydown" && event.key !== "Enter") return
    event?.preventDefault?.()
    if (!this.hasZoomInputTarget) return
    const raw = this.zoomInputTarget.value.trim()
    const value = parseFloat(raw)
    if (!Number.isFinite(value)) {
      this.syncZoomControls()
      return
    }
    const clamped = Math.max(0, Math.min(22, value))
    this.getMap()?.setZoom(clamped)
    this.syncZoomControls()
  }

  syncZoomControls() {
    const map = this.getMap()
    if (!map) return
    const z = map.getZoom()
    const display = z.toFixed(1).replace(/\.0$/, "")
    // Always update the input — it's a number display, never user-dragged.
    if (this.hasZoomInputTarget && document.activeElement !== this.zoomInputTarget) {
      this.zoomInputTarget.value = display
    }
    // Skip the slider write only when the slider is being actively dragged
    // (the slider's own drag fires `input` events that already update its value).
    if (this.hasZoomSliderTarget && document.activeElement !== this.zoomSliderTarget) {
      this.zoomSliderTarget.value = z.toFixed(1)
    }
  }

  // --- draw + selection ---

  initDraw(map) {
    if (this._draw) return
    const DrawCtor = window.MapboxDraw
    if (!DrawCtor) return  // CDN not loaded yet; silent no-op, user won't see a broken UI
    this._draw = new DrawCtor({
      displayControlsDefault: false
      // We drive mode changes via our own menu; Draw's default control bar is hidden.
    })
    map.addControl(this._draw, "top-right")
    map.on("draw.create", (e) => this.handleDrawCreate(e))
  }

  startPolygonDraw(event) {
    event?.preventDefault?.()
    this._draw?.changeMode("draw_polygon")
    this.closeAllDropdowns()
  }

  stopDraw(event) {
    event?.preventDefault?.()
    this._draw?.changeMode("simple_select")
    this._draw?.deleteAll()
    this.closeAllDropdowns()
  }

  closeAllDropdowns() {
    this.element.querySelectorAll("[data-controller='dropdown']").forEach(wrapper => {
      const ctrl = window.Stimulus?.getControllerForElementAndIdentifier?.(wrapper, "dropdown")
      if (ctrl) ctrl.close()
      else wrapper.querySelector("[data-dropdown-target='menu']")?.classList.add("hidden")
    })
  }

  handleDrawCreate(e) {
    const features = e.features || []
    if (!features.length) return
    const polygons = features.filter(f => f.geometry?.type === "Polygon")
    if (!polygons.length) return

    const places = this._lastGeojson?.features || []
    polygons.forEach(poly => {
      const ring = poly.geometry.coordinates[0]
      places.forEach(place => {
        if (!place.geometry) return
        const [lng, lat] = place.geometry.coordinates
        if (this.pointInRing([lng, lat], ring)) {
          this._selectedIds.add(place.properties.id)
        }
      })
    })

    // Clear the drawing — selection is the artefact, not the shape.
    this._draw?.deleteAll()
    this._draw?.changeMode("simple_select")
    this.syncSelectionUI()
  }

  // Ray-cast point-in-ring. Ring is [[lng,lat], ...]. Performs fine for our scale
  // (12k places worst case) without pulling Turf in as a dependency.
  pointInRing(point, ring) {
    const [x, y] = point
    let inside = false
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i]
      const [xj, yj] = ring[j]
      const intersect = ((yi > y) !== (yj > y)) &&
                        (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)
      if (intersect) inside = !inside
    }
    return inside
  }

  toggleSelection(placeId) {
    if (this._selectedIds.has(placeId)) this._selectedIds.delete(placeId)
    else this._selectedIds.add(placeId)
    this.syncSelectionUI()
  }

  clearSelection(event) {
    event?.preventDefault?.()
    this._selectedIds.clear()
    this.syncSelectionUI()
  }

  syncSelectionUI() {
    const size = this._selectedIds.size

    if (this.hasSelectionCountTarget) this.selectionCountTarget.textContent = String(size)
    if (this.hasActionActiveDotTarget) this.actionActiveDotTarget.classList.toggle("hidden", size === 0)

    // Auto-open the action menu when selection grows from zero, so the user sees
    // the count land. Never auto-close — the toggle button controls close.
    if (size > 0 && this.hasDrawerTarget && this.drawerTarget.classList.contains("hidden")) {
      const wrapper = this.drawerTarget.closest("[data-controller='dropdown']")
      const ctrl = wrapper && window.Stimulus?.getControllerForElementAndIdentifier?.(wrapper, "dropdown")
      if (ctrl?.show) ctrl.show()
      else this.drawerTarget.classList.remove("hidden")
    }

    this.applySelectionLayerFilter()
  }

  applySelectionLayerFilter() {
    const map = this.getMap()
    if (!map || !map.getLayer) return
    if (!map.getLayer("atlas-points-selected")) return
    const ids = Array.from(this._selectedIds)
    // Empty list -> filter matches nothing (["in", id, []] evaluates false), layer hides.
    map.setFilter("atlas-points-selected", ["in", ["get", "id"], ["literal", ids]])
    // Dim the base layer when any selection is active — "context, not disabled".
    const hasSelection = ids.length > 0
    if (map.getLayer("atlas-points")) {
      map.setPaintProperty("atlas-points", "circle-opacity", hasSelection ? 0.35 : 0.9)
    }
  }

  handlePointClick(map, e) {
    const feature = e.features?.[0]
    if (!feature) return
    const placeId = feature.properties?.id
    const shift = e.originalEvent?.shiftKey

    // Shift-click always adds/toggles membership without opening the popup.
    if (shift) {
      this.toggleSelection(placeId)
      return
    }

    // If this feature is already selected, plain click removes it.
    if (this._selectedIds.has(placeId)) {
      this._selectedIds.delete(placeId)
      this.syncSelectionUI()
      return
    }

    // No special modifier + not selected → open the rich card as before.
    this.showPopup(map, e)
  }

  // --- private ---

  applyFilters(url) {
    const selects = this.element.querySelectorAll("[data-atlas-filter]")
    selects.forEach(sel => {
      const key = sel.dataset.atlasFilter
      if (sel.multiple) {
        const selected = Array.from(sel.selectedOptions).map(o => o.value).filter(v => v)
        selected.forEach(v => url.searchParams.append(`${key}[]`, v))
      } else {
        if (sel.value) url.searchParams.set(key, sel.value)
      }
    })
  }

  initLayer() {
    const map = this.getMap()
    if (!map) {
      setTimeout(() => this.initLayer(), 300)
      return
    }

    const load = () => {
      map.setCenter([133.7751, -25.2744])
      map.setZoom(4)
      map.on("zoom", () => this.syncZoomControls())
      this.initDraw(map)
      this._skipNextFit = true
      this.reloadPlaces()
      if (this._savedStyleKey) {
        const saved = this.styleOptionFor(this._savedStyleKey)
        if (saved && saved.url !== this.mapStyleValue) {
          this.applyStyle(saved.url, saved.key)
        } else {
          this.markActiveStyle(this._savedStyleKey)
        }
      } else {
        this.markActiveStyle(this.defaultStyleKey())
      }
      this.syncZoomControls()
    }

    if (map.loaded()) {
      load()
    } else {
      map.on("load", load)
    }
  }

  changeStyle(event) {
    const { styleUrl: url, styleKey: key } = event.params
    if (!url || this._styleLoading) return
    this.applyStyle(url, key, event.currentTarget)
    this.saveStyleKey(key)
  }

  applyStyle(url, key, clickedButton = null) {
    const map = this.getMap()
    if (!map) return
    this.popup?.remove()
    this.hoverPopup?.remove()

    this.setStyleLoading(key, true)
    this.mapStyleValue = url

    const finish = () => {
      if (!this._styleLoading) return
      this.setStyleLoading(key, false)
      this.markActiveStyle(key)
      this.closeStyleMenu(clickedButton)
      this._skipNextFit = true
      this.reloadPlaces()
    }

    map.once("style.load", finish)
    // Safety net in case style.load never fires (bad URL, network stall).
    this._styleLoadTimeout = setTimeout(finish, 5000)
    map.setStyle(url)
  }

  setStyleLoading(key, isLoading) {
    this._styleLoading = isLoading
    if (!isLoading && this._styleLoadTimeout) {
      clearTimeout(this._styleLoadTimeout)
      this._styleLoadTimeout = null
    }
    this.element.querySelectorAll("[data-atlas-style-spinner]").forEach(el => {
      el.classList.toggle("hidden", !(isLoading && el.dataset.atlasStyleSpinner === key))
    })
    if (isLoading) {
      this.element.querySelectorAll("[data-atlas-style-check]").forEach(el => el.classList.add("hidden"))
    }
    this.element.querySelectorAll("[data-atlas-style-option]").forEach(btn => {
      btn.disabled = isLoading
    })
  }

  closeStyleMenu(clickedButton) {
    const wrapper = clickedButton?.closest("[data-controller='dropdown']")
    const ctrl = wrapper && window.Stimulus?.getControllerForElementAndIdentifier?.(wrapper, "dropdown")
    if (ctrl) {
      ctrl.close()
    } else {
      wrapper?.querySelector("[data-dropdown-target='menu']")?.classList.add("hidden")
    }
  }

  // Mutual exclusion between the floating filter panel and the style switcher —
  // prevents the two popovers from overlapping each other when opened in sequence.
  closeOtherPanels(event) {
    const currentWrapper = event.currentTarget.closest("[data-controller='dropdown']")
    this.element.querySelectorAll("[data-controller='dropdown']").forEach(wrapper => {
      if (wrapper === currentWrapper) return
      const ctrl = window.Stimulus?.getControllerForElementAndIdentifier?.(wrapper, "dropdown")
      if (ctrl) {
        ctrl.close()
      } else {
        wrapper.querySelector("[data-dropdown-target='menu']")?.classList.add("hidden")
      }
    })
  }

  markActiveStyle(activeKey) {
    this.element.querySelectorAll("[data-atlas-style-check]").forEach(el => {
      el.classList.toggle("hidden", el.dataset.atlasStyleCheck !== activeKey)
    })
    this.element.querySelectorAll("[data-atlas-style-option]").forEach(btn => {
      btn.classList.toggle("bg-slate-50", btn.dataset.atlasStyleOption === activeKey)
    })
  }

  styleOptionFor(key) {
    const btn = this.element.querySelector(`[data-atlas-style-option='${key}']`)
    if (!btn) return null
    return { key, url: btn.dataset.atlasStyleUrlParam }
  }

  defaultStyleKey() {
    const btn = this.element.querySelector(`[data-atlas-style-url-param='${this.mapStyleValue}']`)
    return btn?.dataset.atlasStyleOption || "outdoors"
  }

  readSavedStyleKey() {
    try { return window.localStorage.getItem("atlas:map-style") } catch { return null }
  }

  saveStyleKey(key) {
    try { window.localStorage.setItem("atlas:map-style", key) } catch { /* private mode / disabled storage */ }
  }

  // Toggle the browser Fullscreen API against `this.element` — same target
  // Mapbox's native FullscreenControl was previously bound to via container:.
  // This covers the map AND all floating panels, not just the raw canvas.
  // Escape to exit is handled natively by the browser.
  toggleFullscreen(event) {
    event?.preventDefault?.()
    const el = this.element
    const doc = document
    const isFull = doc.fullscreenElement || doc.webkitFullscreenElement
    if (isFull) {
      (doc.exitFullscreen || doc.webkitExitFullscreen)?.call(doc)
    } else {
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)
    }
  }

  addPlacesLayer(map, geojson) {
    const clusterOn = this.clusteringValue !== false
    map.addSource("atlas-places", {
      type: "geojson",
      data: geojson,
      cluster: clusterOn,
      clusterMaxZoom: 10,
      clusterRadius: 50
    })

    if (clusterOn) {
      map.addLayer({
        id: "atlas-clusters",
        type: "circle",
        source: "atlas-places",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": ["step", ["get", "point_count"], "#0a5a8f", 100, "#003d68", 500, "#002847"],
          "circle-radius": ["step", ["get", "point_count"], 18, 100, 26, 500, 34],
          "circle-opacity": 0.85
        }
      })

      map.addLayer({
        id: "atlas-cluster-count",
        type: "symbol",
        source: "atlas-places",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 12
        },
        paint: { "text-color": "#ffffff" }
      })
    }

    map.addLayer({
      id: "atlas-points",
      type: "circle",
      source: "atlas-places",
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-radius": 6,
        "circle-color": [
          "case",
          ["get", "flagged_bounds"], "#ef4444",
          ["to-boolean", ["get", "_group_color"]], ["get", "_group_color"],
          // Stored retailer colour (card #4107); sapphire when the retailer has none.
          ["to-boolean", ["get", "retailer_colour"]], ["get", "retailer_colour"],
          "#003d68"
        ],
        "circle-stroke-width": 1.5,
        "circle-stroke-color": "#ffffff",
        "circle-opacity": 0.9
      }
    })

    // Selected-places overlay — sapphire fill with a canary ring, sits above base points.
    // Starts with an empty inclusion filter so nothing renders until the user selects.
    map.addLayer({
      id: "atlas-points-selected",
      type: "circle",
      source: "atlas-places",
      filter: ["in", ["get", "id"], ["literal", []]],
      paint: {
        "circle-radius": 8,
        "circle-color": "#0a5a8f",
        "circle-stroke-width": 2.5,
        "circle-stroke-color": "#facc15",
        "circle-opacity": 1
      }
    })

    // Sync filter + base-layer opacity to current selection state (no-op if empty).
    this.applySelectionLayerFilter()

    map.addLayer({
      id: "atlas-labels",
      type: "symbol",
      source: "atlas-places",
      minzoom: this.labelMinZoomValue,
      filter: ["!", ["has", "point_count"]],
      layout: {
        "text-field": ["get", "name"],
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 11,
        "text-offset": [0, 0.9],
        "text-anchor": "top",
        "text-allow-overlap": false,
        "text-optional": true
      },
      paint: {
        "text-color": "#0f172a",
        "text-halo-color": "#ffffff",
        "text-halo-width": 1.5
      }
    })

    if (clusterOn) {
      map.on("click", "atlas-clusters", (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ["atlas-clusters"] })
        const clusterId = features[0].properties.cluster_id
        map.getSource("atlas-places").getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return
          map.easeTo({ center: features[0].geometry.coordinates, zoom })
        })
      })

      map.on("mouseenter", "atlas-clusters", () => map.getCanvas().style.cursor = "pointer")
      map.on("mouseleave", "atlas-clusters", () => map.getCanvas().style.cursor = "")
    }

    map.on("click", "atlas-points", (e) => this.handlePointClick(map, e))

    map.on("mouseenter", "atlas-points", (e) => {
      map.getCanvas().style.cursor = "pointer"
      this.showHover(map, e)
    })
    map.on("mousemove", "atlas-points", (e) => this.showHover(map, e))
    map.on("mouseleave", "atlas-points", () => {
      map.getCanvas().style.cursor = ""
      this.hoverPopup?.remove()
    })
  }

  showHover(map, e) {
    const props = e.features[0].properties
    const coords = e.features[0].geometry.coordinates.slice()
    const clean = (v) => (v && v !== "null") ? v : null
    const subtitle = [clean(props.banner), clean(props.retailer)].filter(Boolean).join(" · ")

    const html = `
      <div style="font-family:system-ui;padding:2px 4px;pointer-events:none;">
        <p style="font-weight:600;font-size:12px;margin:0;color:#0f172a;">${props.name}</p>
        ${subtitle ? `<p style="font-size:11px;color:#64748b;margin:2px 0 0;">${subtitle}</p>` : ""}
      </div>
    `

    if (!this.hoverPopup) {
      this.hoverPopup = new (this.mapboxgl().Popup)({
        closeButton: false,
        closeOnClick: false,
        offset: 14,
        className: "atlas-hover-popup"
      })
    }
    this.hoverPopup.setLngLat(coords).setHTML(html).addTo(map)
  }

  showPopup(map, e) {
    this.popup?.remove()
    this.hoverPopup?.remove()
    const props = e.features[0].properties
    const coords = e.features[0].geometry.coordinates.slice()

    const skeleton = `
      <div style="font-family:system-ui;min-width:360px;padding:8px 2px;">
        <p style="font-weight:700;font-size:15px;margin:0 0 6px;color:#0f172a;">${props.name}</p>
        <div style="display:flex;flex-direction:column;gap:6px;">
          <div style="height:14px;background:#e2e8f0;border-radius:4px;width:80%;"></div>
          <div style="height:14px;background:#e2e8f0;border-radius:4px;width:60%;"></div>
          <div style="height:52px;background:#f1f5f9;border-radius:6px;margin-top:4px;"></div>
        </div>
      </div>
    `

    this.popup = new (this.mapboxgl().Popup)({ offset: 12, maxWidth: "420px", closeButton: false })
      .setLngLat(coords)
      .setHTML(skeleton)
      .addTo(map)

    fetch(`/atlas/card/${props.id}`, {
      credentials: "same-origin",
      headers: { Accept: "text/html" }
    })
      .then(r => r.ok ? r.text() : Promise.reject(r.status))
      .then(html => {
        if (!this.popup?.isOpen()) return
        this.popup.setHTML(html)
        const closeBtn = this.popup.getElement()?.querySelector("[data-popup-close]")
        closeBtn?.addEventListener("click", () => this.popup.remove(), { once: true })
      })
      .catch(err => console.warn("[atlas] card load failed", err))
  }

  fitToData(map, features) {
    const valid = features.filter(f => !f.properties.flagged_bounds)
    if (!valid.length) return

    let minLng = Infinity, maxLng = -Infinity
    let minLat = Infinity, maxLat = -Infinity

    valid.forEach(({ geometry: { coordinates: [lng, lat] } }) => {
      if (lng < minLng) minLng = lng
      if (lng > maxLng) maxLng = lng
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
    })

    if (minLng === maxLng && minLat === maxLat) {
      map.flyTo({ center: [minLng, minLat], zoom: 14 })
    } else {
      map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 60, maxZoom: 15 })
    }
  }

  updateCounts(meta) {
    if (!meta) return
    if (this.hasCountTarget) this.countTarget.textContent = meta.total
    if (this.hasNullCountTarget) this.nullCountTarget.textContent = meta.null_coords
    if (this.hasBoundsCountTarget) this.boundsCountTarget.textContent = meta.out_of_bounds
  }

  readSavedGroupBy() {
    try { return window.localStorage.getItem("atlas:group-by") || "" } catch { return "" }
  }

  saveGroupBy(key) {
    try { window.localStorage.setItem("atlas:group-by", key || "") } catch { /* ignore */ }
  }

  readSavedClustering() {
    try {
      const raw = window.localStorage.getItem("atlas:clustering")
      if (raw === null) return null
      return raw === "true"
    } catch { return null }
  }

  saveClustering(flag) {
    try { window.localStorage.setItem("atlas:clustering", flag ? "true" : "false") } catch { /* ignore */ }
  }

  readSavedLabelZoom() {
    try {
      const raw = window.localStorage.getItem("atlas:label-zoom")
      if (raw === null) return null
      const n = parseInt(raw, 10)
      return Number.isFinite(n) ? n : null
    } catch { return null }
  }

  saveLabelZoom(zoom) {
    try { window.localStorage.setItem("atlas:label-zoom", String(zoom)) } catch { /* ignore */ }
  }

  markActiveLabelZoom(activeZoom) {
    this.element.querySelectorAll("[data-atlas-label-zoom-check]").forEach(el => {
      el.classList.toggle("hidden", parseInt(el.dataset.atlasLabelZoomCheck, 10) !== activeZoom)
    })
  }

  changeLabelZoom(event) {
    const zoom = parseInt(event.params.labelZoom, 10)
    if (!Number.isFinite(zoom)) return
    this.labelMinZoomValue = zoom
    this.saveLabelZoom(zoom)
    this.markActiveLabelZoom(zoom)

    const map = this.getMap()
    if (map && map.getLayer("atlas-labels")) {
      map.setLayerZoomRange("atlas-labels", zoom, 24)
    }

    const wrapper = event.currentTarget.closest("[data-controller='dropdown']")
    const ctrl = wrapper && window.Stimulus?.getControllerForElementAndIdentifier?.(wrapper, "dropdown")
    if (ctrl) ctrl.close()
  }

  applyGroupBy() {
    const map = this.getMap()
    if (!map || !this._lastGeojson) { this.updateLegend([]); return }
    const enriched = this.assignGroupColors(this._lastGeojson, this.groupByValue)
    const source = map.getSource("atlas-places")
    if (source) source.setData(enriched)
    this.updateLegend(enriched.features)
  }

  assignGroupColors(geojson, groupBy) {
    const features = geojson.features.map(f => ({ ...f, properties: { ...f.properties } }))

    if (!groupBy) {
      features.forEach(f => {
        f.properties._group_color = null
        f.properties._group_value = null
        f.properties._group_label = null
      })
      this._lastBuckets = new Map()
      return { ...geojson, features }
    }

    const palette = this.constructor.PALETTE
    const unassignedColor = this.constructor.UNASSIGNED_COLOR
    const buckets = new Map() // key -> { label, count, color }

    // Phase 1 — count only, no colour assigned yet.
    features.forEach(f => {
      const { value, label } = this.groupValueFor(f.properties, groupBy)
      let key
      let resolvedLabel
      if (value === null || value === undefined) {
        key = "__unassigned__"
        resolvedLabel = "Unassigned"
      } else {
        key = String(value)
        resolvedLabel = label
      }
      if (!buckets.has(key)) buckets.set(key, { label: resolvedLabel, count: 0, color: null })
      const bucket = buckets.get(key)
      bucket.count += 1
      // Grouping by retailer keeps each retailer's stored colour (card #4107) so the
      // legend matches the plain map; retailers without one fall through to the palette.
      if (groupBy === "retailer" && !bucket.color && /^#[0-9a-fA-F]{6}$/.test(f.properties.retailer_colour || "")) {
        bucket.color = f.properties.retailer_colour
      }
      f.properties._bucket_key = key
      f.properties._group_value = key === "__unassigned__" ? null : key
      f.properties._group_label = resolvedLabel
    })

    // Phase 2 — assign colours by rank. Unassigned reserved; top-N distinct; tail HSL ramp.
    const unassigned = buckets.get("__unassigned__")
    if (unassigned) unassigned.color = unassignedColor

    const dataEntries = Array.from(buckets.entries())
      .filter(([k, info]) => k !== "__unassigned__" && !info.color)
      .sort((a, b) => b[1].count - a[1].count)

    const topCount = Math.min(palette.length, dataEntries.length)
    for (let i = 0; i < topCount; i++) {
      dataEntries[i][1].color = palette[i]
    }

    const tail = dataEntries.slice(topCount)
    if (tail.length) {
      const counts = tail.map(([_, info]) => info.count)
      const minTail = Math.min(...counts)
      const maxTail = Math.max(...counts)
      const range = Math.max(1, maxTail - minTail)
      tail.forEach(([_, info]) => {
        const t = (info.count - minTail) / range   // 0..1 (darker for more records)
        const L = Math.round(78 - 30 * t)          // 78% (lightest) -> 48% (darkest)
        info.color = `hsl(215, 14%, ${L}%)`
      })
    }

    // Phase 3 — stamp each feature with its bucket's resolved colour.
    features.forEach(f => {
      const bucket = buckets.get(f.properties._bucket_key)
      f.properties._group_color = bucket ? bucket.color : null
      delete f.properties._bucket_key
    })

    this._lastBuckets = buckets
    return { ...geojson, features }
  }

  groupValueFor(props, groupBy) {
    const labels = this.groupLabelsValue || {}
    switch (groupBy) {
      case "retailer":
        return { value: props.retailer_id, label: labels.retailer?.[props.retailer_id] || `Retailer ${props.retailer_id}` }
      case "banner":
        return { value: props.banner_id, label: labels.banner?.[props.banner_id] || `Banner ${props.banner_id}` }
      case "place_owner":
        return { value: props.place_owner_id, label: labels.place_owner?.[props.place_owner_id] || `Owner ${props.place_owner_id}` }
      case "tasker": {
        const ids = props.tasker_ids
        if (!ids || !ids.length) return { value: null, label: null }
        const first = ids[0]
        return { value: first, label: labels.tasker?.[first] || `Tasker ${first}` }
      }
      case "place_group": {
        const ids = props.place_group_ids
        if (!ids || !ids.length) return { value: null, label: null }
        const first = ids[0]
        return { value: first, label: labels.place_group?.[first] || `Group ${first}` }
      }
      case "priority_band": {
        const p = props.priority
        if (p === null || p === undefined) return { value: null, label: null }
        const band = Math.floor(p / 100) * 100
        return { value: `${band}-${band + 99}`, label: `${band}-${band + 99}` }
      }
      case "visit_frequency": {
        const value = props.visit_frequency
        if (value === null || value === undefined || value === "") return { value: null, label: null }
        const label = (this.groupLabelsValue?.visit_frequency?.[value]) || String(value)
        return { value, label }
      }
      case "response_status": {
        const value = props.response_status
        if (value === null || value === undefined || value === "") return { value: null, label: null }
        const label = (this.groupLabelsValue?.response_status?.[value]) || String(value)
        return { value, label }
      }
      default:
        return { value: null, label: null }
    }
  }

  updateLegend(_features) {
    if (!this.hasLegendTarget) return
    if (!this.groupByValue) {
      this.legendTarget.classList.add("hidden")
      if (this.hasLegendBodyTarget) this.legendBodyTarget.innerHTML = ""
      return
    }

    const titleMap = {
      tasker: "Tasker", retailer: "Retailer", banner: "Banner",
      place_owner: "Place Owner", priority_band: "Priority band", place_group: "Place Group",
      visit_frequency: "Visit Frequency", response_status: "Response Status"
    }
    if (this.hasLegendTitleTarget) {
      this.legendTitleTarget.textContent = titleMap[this.groupByValue] || "Group by"
    }

    const buckets = this._lastBuckets || new Map()
    const sorted = Array.from(buckets.values()).sort((a, b) => b.count - a.count)
    const rows = sorted.map(info => {
      const isUnassigned = info.label === "Unassigned"
      const labelClass = isUnassigned ? "text-slate-500 italic" : "text-slate-700"
      const safeLabel = String(info.label).replace(/</g, "&lt;").replace(/>/g, "&gt;")
      return `
        <div class="flex items-center gap-1.5 whitespace-nowrap">
          <span class="inline-block size-3 rounded-full shrink-0" style="background-color: ${info.color};"></span>
          <span class="${labelClass}" title="${safeLabel}">${safeLabel}</span>
          <span class="text-slate-400">${info.count}</span>
        </div>
      `
    }).join("")
    if (this.hasLegendBodyTarget) this.legendBodyTarget.innerHTML = rows
    this.legendTarget.classList.toggle("hidden", sorted.length === 0)
  }

  getMap() {
    const mapkick = window.Mapkick
    if (!mapkick) return null
    const instance = mapkick.maps[this.mapIdValue]
    return instance ? instance.map : null
  }

  mapboxgl() {
    return window.Mapkick?.library
  }
}
